import { useState } from 'react';

const ENDPOINT = import.meta.env.VITE_GET_SIGNED_URL_FOR_MANUFACTURER;

const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
const ACCEPTED_VIDEO_TYPES = ['video/mp4', 'video/webm', 'video/quicktime'];
const MAX_IMAGE_SIZE = 5 * 1024 * 1024;
const MAX_VIDEO_SIZE = 50 * 1024 * 1024;

export default function useManufacturerBrandingUpload() {
  const [uploading, setUploading] = useState({ logo: false, media: false });
  const [progress, setProgress] = useState({ logo: 0, media: {} });
  const [errors, setErrors] = useState({ logo: null, media: null });

  function validateFile(file, type = 'logo') {
    if (type === 'logo') {
      if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
        throw new Error('Logo must be an image (JPEG, PNG, GIF, or WEBP)');
      }
      if (file.size > MAX_IMAGE_SIZE) {
        throw new Error('Logo image must be smaller than 5MB');
      }
    } else {
      const acceptedTypes = [...ACCEPTED_IMAGE_TYPES, ...ACCEPTED_VIDEO_TYPES];
      if (!acceptedTypes.includes(file.type)) {
        throw new Error('Media files must be images or videos (JPEG, PNG, GIF, WEBP, MP4, WEBM)');
      }

      const maxSize = file.type.startsWith('video/') ? MAX_VIDEO_SIZE : MAX_IMAGE_SIZE;
      if (file.size > maxSize) {
        throw new Error(`File too large (max ${file.type.startsWith('video/') ? '50MB' : '5MB'})`);
      }
    }
    return true;
  }

  async function uploadFile(file, brandName, fileType = 'logo', index = 0) {
    try {
      validateFile(file, fileType);

      setUploading(prev => ({ ...prev, [fileType]: true }));
      setProgress(prev => ({
        ...prev,
        [fileType]: fileType === 'logo' ? 0 : { ...prev.media, [index]: 0 }
      }));

      const ext = file.name.split('.').pop() || '';
      const baseName = fileType === 'logo' ? 'logo' : `media_${index}`;
      const safeName = `${baseName.replace(/\s+/g, '_')}.${ext}`;

      const payload = {
        brandName,
        fileName: safeName,
        type: file.type
      };

      const response = await fetch(ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': import.meta.env.VITE_X_API_KEY 
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.message || 'Failed to get signed URL');
      }

      const { signedUrl, fileUrl } = await response.json();
      setProgress(prev => ({
        ...prev,
        [fileType]: fileType === 'logo' ? 30 : { ...prev.media, [index]: 30 }
      }));

      const uploadResponse = await fetch(signedUrl, {
        method: 'PUT',
        headers: { 'Content-Type': file.type },
        body: file,
      });

      if (!uploadResponse.ok) {
        throw new Error('Failed to upload file');
      }

      setProgress(prev => ({
        ...prev,
        [fileType]: fileType === 'logo' ? 100 : { ...prev.media, [index]: 100 }
      }));

      return fileUrl || signedUrl.split('?')[0];
    } catch (error) {
      console.error(`${fileType} upload error:`, error);
      setErrors(prev => ({ ...prev, [fileType]: error.message }));
      throw error;
    } finally {
      setUploading(prev => ({ ...prev, [fileType]: false }));
    }
  }

  return {
    uploading,
    progress,
    errors,
    uploadFile,
    acceptedTypes: {
      logo: ACCEPTED_IMAGE_TYPES,
      media: [...ACCEPTED_IMAGE_TYPES, ...ACCEPTED_VIDEO_TYPES]
    }
  };
}